import { useCallback, useState } from 'react';

interface UseDraftStateOption {
  alwaysCommit?: boolean;
}

/**
 * A small hook that encapsulates a `useState` and a `commit` function. This is useful
 * for managing draft states in components that might need to be committed to parent
 * states in a controlled manner. The `alwaysCommit` option can be used to force a commit
 * on every state change as a quick/dynamic opt-out of the drafting behaviour.
 */
export const useDraftState = <T>(
  initialState: T,
  onCommit: (state: T) => void,
  options?: UseDraftStateOption,
) => {
  const { alwaysCommit = false } = options ?? {};
  const [draftState, _setDraftState] = useState<T>(initialState);

  const commit = useCallback(() => {
    onCommit(draftState);
  }, [draftState, onCommit]);

  const setDraftState = useCallback(
    (state: T) => {
      _setDraftState(state);
      if (alwaysCommit) {
        onCommit(state);
      }
    },
    [alwaysCommit, onCommit],
  );

  return { draftState, setDraftState, commit };
};
